import type { Brand } from '../lib/api.js';

// Small inline brand-identity pill used wherever multiple brands'
// content is collated into one surface (notifications panel, future
// global activity feeds, etc.). Mirrors the brand-switcher chip in
// the top-nav at a smaller scale so the visual language stays
// consistent: same favicon-in-a-circle treatment, same plum-dot
// fallback for brands without a logo.
//
// Three rendered states (driven by inputs):
//   - brand is a hydrated Brand row → favicon (or logo) + lowercase
//     brand name.
//   - brand is null AND fallbackName looks like a brand id ("…") →
//     placeholder pill in paper bg. brandsQuery is still loading;
//     the next render with the cache filled resolves the chip.
//   - brand is null AND fallbackName === 'magig' → workspace-scope
//     marker (e.g. org-wide notifications). Neutral plum dot, no
//     favicon expected.
//
// Inline-only — the pill is meant to ride alongside other small
// metadata (kind tag, timestamp) on a single line. Don't grow it
// into a standalone surface; use BrandSwitcher for that.

export interface BrandChipProps {
  brand: Brand | null;
  /** Shown when `brand` is null. Caller passes "magig" for org-scope
   *  rows and "…" (or similar) for in-flight brand lookups. */
  fallbackName: string;
}

export function BrandChip({ brand, fallbackName }: BrandChipProps) {
  if (!brand) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[var(--line)] bg-[var(--paper)] px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/65">
        <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--plum)]" />
        {fallbackName.toLowerCase()}
      </span>
    );
  }
  const icon = brand.faviconUrl ?? brand.logoUrl;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[var(--line)] bg-white px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/75">
      {icon ? (
        <img
          src={icon}
          alt=""
          className="h-3 w-3 rounded-full border border-[var(--line)] object-cover"
        />
      ) : (
        <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--plum)]" />
      )}
      <span className="max-w-[120px] truncate">{brand.name.toLowerCase()}</span>
    </span>
  );
}
